<script setup>
import { ref } from 'vue';
import { LANDING_CONTENT } from '@/services/landing-content';

const openIndex = ref(null);

// Use retail FAQ content directly
const faqItems = [
  ...LANDING_CONTENT.retail.faq,
  ...LANDING_CONTENT.common.faqCommon
];

const toggleFAQ = (index) => {
  openIndex.value = openIndex.value === index ? null : index;
};
</script>

<template>
  <section class="faq-section">
    <div class="faq-container">
      <h2 class="section-title">Frequently asked questions</h2>

      <div class="faq-list">
        <div
          v-for="(item, index) in faqItems"
          :key="index"
          class="faq-item"
          :class="{ open: openIndex === index }"
        >
          <button
            class="faq-question"
            @click="toggleFAQ(index)"
            :aria-expanded="openIndex === index"
          >
            <span>{{ item.question }}</span>
            <i :class="`pi ${openIndex === index ? 'pi-minus' : 'pi-plus'}`"></i>
          </button>
          <div v-if="openIndex === index" class="faq-answer">
            {{ item.answer }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq-section {
  padding: 80px 24px;
  background: #f9fafb;
}

.faq-container {
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin: 0 0 48px 0;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: white;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  transition: border-color 0.2s ease;
}

.faq-item.open {
  border-color: #3b82f6;
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: none;
  border: none;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  cursor: pointer;
  transition: color 0.2s ease;
}

.faq-question:hover {
  color: #3b82f6;
}

.faq-question i {
  font-size: 0.875rem;
  color: #3b82f6;
  flex-shrink: 0;
  margin-left: 16px;
}

.faq-answer {
  padding: 0 24px 20px 24px;
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .faq-section {
    padding: 48px 16px;
  }

  .section-title {
    font-size: 1.75rem;
    margin-bottom: 32px;
  }

  .faq-question {
    padding: 16px 20px;
    font-size: 1rem;
  }

  .faq-question i {
    font-size: 0.8125rem;
    margin-left: 12px;
  }

  .faq-answer {
    padding: 0 20px 16px 20px;
    font-size: 0.9375rem;
  }
}

@media (max-width: 480px) {
  .faq-section {
    padding: 40px 12px;
  }

  .section-title {
    font-size: 1.5rem;
    margin-bottom: 28px;
  }

  .faq-question {
    padding: 14px 16px;
    font-size: 0.9375rem;
  }

  .faq-answer {
    padding: 0 16px 14px 16px;
    font-size: 0.875rem;
  }
}
</style>
